// Sistema de armazenamento persistente por dispositivo
export interface DeviceOrder {
  orderId: string
  customerName: string
  customerPhone: string
  items: Array<{
    prato: {
      nome: string
      preco: number
      descricao?: string
    }
    quantidade: number
  }>
  total: number
  status: string
  paymentMethod: string
  createdAt: string
  updatedAt: string
  localId: string
  estabelecimentoId?: string
}

export interface DeviceData {
  deviceId: string
  orders: DeviceOrder[]
  activeOrders: string[]
  lastActivity: number
}

const DEVICE_DATA_KEY = "device_orders_data"
const DEVICE_ID_KEY = "device_unique_id"

// Gerar ID único do dispositivo
export function generateDeviceId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 15)
  const userAgent =
    typeof window !== "undefined" ? window.navigator.userAgent.replace(/[^a-zA-Z0-9]/g, "").substring(0, 10) : "unknown"

  return `device_${timestamp}_${random}_${userAgent}`.toLowerCase()
}

// Obter ou criar ID do dispositivo
export function getDeviceId(): string {
  if (typeof window === "undefined") return generateDeviceId()

  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY)
    if (!deviceId) {
      deviceId = generateDeviceId()
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    }
    return deviceId
  } catch (error) {
    console.error("Erro ao obter device ID:", error)
    return generateDeviceId()
  }
}

// Salvar dados do dispositivo
export function saveDeviceData(data: DeviceData): void {
  if (typeof window === "undefined") return

  try {
    const dataToSave = {
      ...data,
      lastActivity: Date.now(),
    }
    localStorage.setItem(DEVICE_DATA_KEY, JSON.stringify(dataToSave))
    console.log("[DeviceStorage] Dados salvos:", dataToSave.orders.length, "pedidos")
  } catch (error) {
    console.error("Erro ao salvar dados do dispositivo:", error)
  }
}

// Carregar dados do dispositivo
export function loadDeviceData(): DeviceData | null {
  if (typeof window === "undefined") return null

  try {
    const data = localStorage.getItem(DEVICE_DATA_KEY)
    if (data) {
      const parsed = JSON.parse(data) as DeviceData
      console.log("[DeviceStorage] Dados carregados:", parsed.orders.length, "pedidos")
      return parsed
    }
  } catch (error) {
    console.error("Erro ao carregar dados do dispositivo:", error)
  }

  return null
}

// Adicionar pedido aos dados do dispositivo
export function addOrderToDevice(order: DeviceOrder): void {
  const deviceId = getDeviceId()
  const existingData = loadDeviceData()

  const deviceData: DeviceData = existingData || {
    deviceId,
    orders: [],
    activeOrders: [],
    lastActivity: Date.now(),
  }

  // Verificar se o pedido já existe
  const existingOrderIndex = deviceData.orders.findIndex((o) => o.orderId === order.orderId)

  if (existingOrderIndex >= 0) {
    // Atualizar pedido existente
    deviceData.orders[existingOrderIndex] = order
  } else {
    // Adicionar novo pedido
    deviceData.orders.unshift(order)
  }

  // Adicionar aos pedidos ativos se não estiver finalizado
  if (!["Entregue", "Finalizado", "Cancelado"].includes(order.status)) {
    if (!deviceData.activeOrders.includes(order.orderId)) {
      deviceData.activeOrders.push(order.orderId)
    }
  } else {
    // Remover dos pedidos ativos se finalizado
    deviceData.activeOrders = deviceData.activeOrders.filter((id) => id !== order.orderId)
  }

  // Manter apenas os últimos 100 pedidos
  deviceData.orders = deviceData.orders.slice(0, 100)

  saveDeviceData(deviceData)
}

// Atualizar status de um pedido
export function updateOrderStatus(orderId: string, status: string): void {
  const deviceData = loadDeviceData()
  if (!deviceData) return

  const orderIndex = deviceData.orders.findIndex((o) => o.orderId === orderId)
  if (orderIndex >= 0) {
    deviceData.orders[orderIndex].status = status
    deviceData.orders[orderIndex].updatedAt = new Date().toISOString()

    // Remover dos ativos se finalizado
    if (["Entregue", "Finalizado", "Cancelado"].includes(status)) {
      deviceData.activeOrders = deviceData.activeOrders.filter((id) => id !== orderId)
    }

    saveDeviceData(deviceData)
  }
}

// Obter todos os pedidos do dispositivo
export function getAllDeviceOrders(): DeviceOrder[] {
  const deviceData = loadDeviceData()
  return deviceData?.orders || []
}

// Obter pedidos ativos
export function getActiveOrders(): DeviceOrder[] {
  const deviceData = loadDeviceData()
  if (!deviceData) return []

  return deviceData.orders.filter(
    (order) =>
      deviceData.activeOrders.includes(order.orderId) &&
      !["Entregue", "Finalizado", "Cancelado"].includes(order.status),
  )
}

// Obter pedido específico
export function getOrderById(orderId: string): DeviceOrder | null {
  const deviceData = loadDeviceData()
  if (!deviceData) return null

  return deviceData.orders.find((order) => order.orderId === orderId) || null
}

// Limpar dados antigos (manter apenas últimos 30 dias)
export function cleanOldOrders(): void {
  const deviceData = loadDeviceData()
  if (!deviceData) return

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000

  deviceData.orders = deviceData.orders.filter((order) => {
    const orderDate = new Date(order.createdAt).getTime()
    return orderDate > thirtyDaysAgo
  })

  // Limpar pedidos ativos que não existem mais
  deviceData.activeOrders = deviceData.activeOrders.filter((orderId) =>
    deviceData.orders.some((order) => order.orderId === orderId),
  )

  saveDeviceData(deviceData)
}

// Exportar dados para backup
export function exportDeviceData(): string {
  const deviceData = loadDeviceData()
  return JSON.stringify(deviceData, null, 2)
}

// Importar dados de backup
export function importDeviceData(jsonData: string): boolean {
  try {
    const data = JSON.parse(jsonData) as DeviceData
    saveDeviceData(data)
    return true
  } catch (error) {
    console.error("Erro ao importar dados:", error)
    return false
  }
}
